// Baselayer Background Checks — litigation/docket detail at
// /background-checks/:id/litigations/:caseId. Reads the docket out of the shared
// docket add-on, shows the case facts, and offers a "Request Docket Details"
// panel (orders docket-detail for this case id); once the matching DocketDetail
// add-on returns, its updates are shown.

import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  DetailLayout,
  DetailSection,
  PageHeader,
  Skeleton,
  StatusBadge,
} from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { useAddOns, useOrderAddOn } from "@/hooks/backgroundChecks/useAddOns";
import {
  findAddOn,
  formatLooseDate,
  DefinitionList,
} from "@/components/backgroundChecks/explorer/tabHelpers";
import {
  parseDockets,
  asArray,
  asObject,
  asString,
  pick,
  display,
  safeParse,
} from "@/components/backgroundChecks/explorer/parse";

export default function LitigationDetailPage() {
  const { id, caseId } = useParams<{ id: string; caseId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const addOnsQuery = useAddOns(id);
  const order = useOrderAddOn(id ?? "");

  if (addOnsQuery.isLoading) {
    return (
      <div className="flex flex-col gap-space-4">
        <Skeleton className="h-12 w-1/2" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  const docketAddOn = findAddOn(addOnsQuery.data, "Litigations", "Bankruptcies");
  const dockets = parseDockets(docketAddOn?.rawResponseJson);
  const docket = dockets.find((d) => d.id === caseId);

  const detailAddOn = findAddOn(addOnsQuery.data, "DocketDetail");
  const detailMatches = !!detailAddOn && docketDetailMatches(detailAddOn.rawResponseJson, caseId);
  const detailPending =
    detailAddOn?.status === "Submitted" || detailAddOn?.status === "Processing";

  if (!docket) {
    return (
      <DetailSection title="Case not found">
        <p className="text-small text-gray-mid m-0">
          This docket could not be found on the current results.
        </p>
        <button
          type="button"
          className="mt-space-3 text-gold-shadow hover:underline text-small"
          onClick={() => navigate(`/background-checks/${id}`)}
        >
          Back to results
        </button>
      </DetailSection>
    );
  }

  const handleRequestDetails = async () => {
    if (!caseId) return;
    try {
      await order.mutateAsync({
        addOnType: "DocketDetail",
        options: { caseId },
      });
      toast({ message: "Docket details requested.", tone: "success" });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.errors[0] ?? err.message
          : err instanceof Error
            ? err.message
            : "Could not request docket details.";
      toast({ message, tone: "error" });
    }
  };

  const updates = detailMatches
    ? asArray(
        pick(asObject(safeParse(detailAddOn!.rawResponseJson)), "updates", "entries", "events"),
      )
    : [];

  return (
    <DetailLayout
      header={
        <PageHeader
          breadcrumbs={[
            { label: "Background Checks", to: "/background-checks" },
            { label: "Results", to: `/background-checks/${id}` },
            { label: docket.docketNumber ?? "Case" },
          ]}
          eyebrow={docket.isBankruptcy ? "Bankruptcy" : "Litigation"}
          title={docket.title ?? docket.docketNumber ?? "Docket"}
          actions={
            docket.status ? (
              <StatusBadge tone="info">{docket.status}</StatusBadge>
            ) : undefined
          }
        />
      }
      aside={
        <DetailSection title="Case">
          <DefinitionList
            items={[
              { label: "Date filed", value: formatLooseDate(docket.dateFiled) },
              { label: "Status", value: docket.status ?? "—" },
              { label: "Court", value: docket.court ?? "—" },
              {
                label: "Case type",
                value:
                  docket.caseType ?? docket.bankruptcyType ?? "—",
              },
              { label: "Docket #", value: docket.docketNumber ?? "—" },
            ]}
          />
        </DetailSection>
      }
    >
      <DetailSection
        title="Docket details"
        accessory={
          !detailMatches ? (
            <Button
              type="button"
              size="sm"
              loading={order.isPending || detailPending}
              disabled={order.isPending || detailPending}
              onClick={handleRequestDetails}
            >
              Request Docket Details
            </Button>
          ) : undefined
        }
      >
        {detailMatches ? (
          updates.length === 0 ? (
            <p className="text-small text-gray-mid m-0">
              No docket entries were returned.
            </p>
          ) : (
            <ol className="m-0 list-none flex flex-col gap-space-3">
              {updates.map((u, i) => {
                const o = asObject(u);
                return (
                  <li
                    key={i}
                    className="border-l-2 border-border-light pl-space-3"
                  >
                    <span className="block text-caption text-gray-mid">
                      {formatLooseDate(
                        asString(pick(o, "date", "filed_date", "timestamp")),
                      )}
                    </span>
                    <span className="block text-small text-onyx">
                      {display(pick(o, "description", "text", "title", "entry"))}
                    </span>
                  </li>
                );
              })}
            </ol>
          )
        ) : detailPending ? (
          <p className="text-small text-gray-mid m-0">
            Baselayer is fetching the full docket. Details appear here once
            ready.
          </p>
        ) : (
          <p className="text-small text-gray-mid m-0">
            Request the full docket to pull every case entry and exhibit
            availability.
          </p>
        )}
      </DetailSection>
    </DetailLayout>
  );
}

/** True when the DocketDetail add-on's payload is for this case id. */
function docketDetailMatches(
  rawResponseJson: string | null | undefined,
  caseId: string | undefined,
): boolean {
  if (!caseId) return false;
  const o = asObject(safeParse(rawResponseJson));
  if (!o) return false;
  const docketId = asString(pick(o, "docket_id", "id", "case_id"));
  // If the payload doesn't carry an id we can't disambiguate — show it anyway.
  return docketId == null || docketId === caseId;
}
